import { useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState, type Ref } from "react";
import { useTranslations } from "next-intl";
import { IconDownload, IconPlus, IconTrash } from "@tabler/icons-react";
import {
  CAPABILITY_CATALOGUE,
  CUSTOM_FOUNDATION_CAPABILITIES,
  Capability,
  actionBundleForColumns,
  effectiveActionBundle,
  expandImplied,
  isAssignableCapability,
  missingActionCaps as missingActionCapsOf,
  missingFieldPrerequisites,
} from "@/types/Capability";
import { EventRoleTemplate } from "@/types/EventRoleTemplate";
import { EventRoleTemplateCreationDTO } from "@/types/EventRoleTemplateCreationDTO";
import type { Filter } from "@/types/Filter";
import type { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import { useFields } from "@/providers/FieldsProvider";
import { useFormChanges } from "@/hooks/useFormChanges";
import { useToast } from "@/components/Toast/Toast";
import { isNoValueOperator } from "@/lib/utils/parser";
import { buildGuestFieldOptionGroups } from "@/lib/utils/field-groups";
import {
  ADDITIONAL_FIELDS_PREFIX,
  bareFieldName,
  findFieldByKey,
  getFieldKey,
} from "@/components/ConditionBuilder/condition-utils";
import {
  rowFilterKeyVerdict,
  rowFilterValueVerdict,
} from "@/pages/event/settings/role-templates/rowFilterKeys";
import {
  OPTION_VALUE_TYPES,
  valueSurvivesFieldChange,
} from "@/lib/utils/filterFieldSwap";
import {
  operatorDisplay,
  operatorsForType,
  isStandardOperator,
} from "@/components/ConditionBuilder/operators";
import { FormInput } from "@/components/Inputs/FormInput";
import { MultilineInput } from "@/components/Inputs/MultilineInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { TableCellContent } from "@/components/TableCell/TableCellContent";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { SectionCard } from "@/components/SectionCard/SectionCard";
import { StickyFormFooter } from "@/components/StickyFormFooter/StickyFormFooter";
import { CapabilitySwitchList } from "./CapabilitySwitchList";
import {
  exceedsCap,
  fieldScopeReducer,
  initialFieldScope,
  needsSensitiveConfirm,
  payloadOf,
  type CatalogueEntry,
  type FieldScopeAction,
  type FieldScopePayload,
  type FieldScopeSource,
  type FieldScopeState,
} from "@/lib/authz/fieldExclusion";
import {
  noteSensitiveConfirmed,
  recheckSensitiveAdds,
  type SensitiveConfirmations,
} from "@/lib/authz/sensitiveRecheck";
import { FieldAllowlistDualPane, type FieldCatalogueStatus } from "./FieldAllowlistDualPane";
import { useFieldScopeConfirm } from "./useFieldScopeConfirm";
import {
  RoleTemplateScopePickers,
  type SlottingScopeAllowlists,
} from "./RoleTemplateScopePickers";
import { ImportFromGuestListModal } from "./ImportFromGuestListModal";
import { SurfaceDefaultsSection } from "@/components/SurfaceDefaults/SurfaceDefaultsSection";
import type { RoleSurfaceDefault } from "@/types/RoleSurfaceDefault";
import {
  categorizeViewFields,
  mergeRowFilters,
  replaceRowFilters,
  viewFieldsToAllowlist,
  filterKey,
  serializeRowFilters,
} from "./roleTemplateImport";

const OPERATORS = ["=", "≠", ">", "≥", "<", "≤", "~", "⊙", "∅"];

// Row filters store the legacy glyph vocabulary; map each glyph onto the
// shared operator registry token so the picker can label it with
// `operatorDisplay` — math symbols stay symbols, cryptic glyphs (~ ⊙ ∅)
// show their translated word. Stored filter shape is unchanged.
const GLYPH_TO_TOKEN: Record<string, string> = {
  "=": "=",
  "≠": "!=",
  ">": ">",
  "≥": ">=",
  "<": "<",
  "≤": "<=",
  "~": "contains",
  "⊙": "is_not_empty",
  "∅": "is_empty",
};

const TOKEN_TO_GLYPH: Record<string, string> = Object.fromEntries(
  Object.entries(GLYPH_TO_TOKEN).map(([glyph, token]) => [token, glyph]),
);

/** The registry's operators for a field's type, mapped onto the glyphs the
 *  legacy row-filter model can store (registry-only tokens like `in` are
 *  dropped). No field → the full static list. */
function validGlyphsForField(field: Field | undefined): string[] {
  // Fresh array — callers may append the row's legacy operator to it.
  if (!field) return [...OPERATORS];
  return operatorsForType(field.dataType)
    .map((token) => TOKEN_TO_GLYPH[token])
    .filter((g): g is string => !!g);
}

/** Type-aware operator glyphs for a row filter: the shared registry's
 *  operators for the picked field's data type, restricted to the 9 glyphs the
 *  legacy row-filter model can store. The row's CURRENT operator is kept so a
 *  legacy row never loses its selection — EXCEPT for IMAGE fields, where the
 *  backend-minted object value makes anything but the presence checks
 *  (∅ / ⊙) meaningless, so an invalid stored operator must not be re-offered. */
function operatorGlyphsForField(
  field: Field | undefined,
  current: string,
): string[] {
  const glyphs = validGlyphsForField(field);
  if (
    current &&
    !glyphs.includes(current) &&
    field?.dataType !== DataType.IMAGE
  ) {
    glyphs.push(current);
  }
  return glyphs;
}

/**
 * Built-in guest fields the picker must NOT offer as a row filter.
 *
 * The backend gate (`EventRoleTemplateService.canonicalRowFilterKey`) accepts a
 * bare key only when it is a filterable built-in — the set
 * `GuestService.validateFilterKeys` allows minus the projection-only tokens.
 * Everything here is outside it, so offering it would turn a pick that used to
 * save silently into a hard `invalidRowFilterKey` on Save:
 *
 *  - `id` / `checkInOutCTA` are frontend-only presentation columns;
 *  - `attachments` / `slotting` / `seating` are UI composites with no scalar
 *    behind them;
 *  - `companions` is a projection include: `FilterParser` has no case for it, so
 *    it reads null and `≠`/`∅` would match every guest.
 *
 * Custom (additionalField) defs are always offerable — they resolve through the
 * event's GUEST field catalogue on both sides.
 *
 * `wasWaitlisted` needs no entry: the backend lists it as filterable, but there
 * is no `FIELDS_GUEST` def for it, so it never reaches this picker.
 */
export const NON_FILTERABLE_BUILTIN_FIELDS = new Set([
  "id",
  "checkInOutCTA",
  "attachments",
  "slotting",
  "seating",
  "companions",
  // `loginToken` is projection-only: the backend allow-lists it so a column
  // can ask for it, then subtracts it again in `validateFilterKeys` because
  // it is DERIVED from username+code and no document stores it. Offering it
  // here would hard-fail the save with `invalidRowFilterKey`.
  "loginToken",
]);

/**
 * `Filter.key`'s server-side `@Pattern` caps a stored key at 100 chars, and the
 * `additionalFields.` prefix costs 17 — while a custom field name may be 100
 * chars long. Such a field can never be a row filter in EITHER shape (the
 * qualified form dies in bean validation, the bare form in the save gate's
 * length re-check), so offering it would be a guaranteed hard save error.
 */
const MAX_ROW_FILTER_KEY_LENGTH = 100;

/** The fields a row filter may target — see NON_FILTERABLE_BUILTIN_FIELDS. */
export function rowFilterableFields(fields: Field[]): Field[] {
  return fields.filter(
    (f) =>
      getFieldKey(f).length <= MAX_ROW_FILTER_KEY_LENGTH &&
      (f.additionalField || !NON_FILTERABLE_BUILTIN_FIELDS.has(f.name)),
  );
}

export function RowFilterRow({
  filter,
  idx,
  fields,
  valuePlaceholder,
  flash,
  onUpdate,
  onRemove,
}: {
  filter: Filter;
  idx: number;
  /**
   * The event's field catalogue. A PROP, not `useFields()`: this row renders in
   * hosts (and tests) that have no `FieldsProvider` above them.
   *
   * Options, labels and resolution are all derived HERE, from the WIRE key
   * (`getFieldKey`) — the two hosts used to each build their own
   * `allFields.map((f) => f.name)` option list, which is what stored the bare
   * custom-field name and broke every lane downstream.
   */
  fields: Field[];
  valuePlaceholder: string;
  /** Briefly highlight this row after it was just imported. */
  flash?: boolean;
  onUpdate: (patch: Partial<Filter>) => void;
  onRemove: () => void;
}) {
  const tRules = useTranslations("rulesAndLimits");
  const tFields = useTranslations("guest-fields");
  const tGroups = useTranslations("fieldGroups");
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const opRef = useRef<HTMLInputElement | null>(null);
  // Hybrid operator labels (design law: math symbols stay symbols, cryptic
  // glyphs show their translated word) — shared vocabulary via operatorDisplay.
  const operatorLabels = useMemo(
    () =>
      Object.fromEntries(
        OPERATORS.map((glyph) => [
          glyph,
          operatorDisplay((k) => tRules(k), GLYPH_TO_TOKEN[glyph]),
        ]),
      ),
    [tRules],
  );
  // Only what the backend gate accepts (see NON_FILTERABLE_BUILTIN_FIELDS).
  const pickableFields = useMemo(() => rowFilterableFields(fields), [fields]);
  // Options are WIRE keys: `additionalFields.<name>` for custom fields, the bare
  // name for built-ins. This is the single place the stored key is decided.
  const fieldOptions = useMemo(
    () => pickableFields.map(getFieldKey),
    [pickableFields],
  );
  // Labels MUST be keyed by the same wire key as the options — a label map keyed
  // by bare name would leave every custom option rendering its raw
  // `additionalFields.tier` text in the dropdown.
  const fieldLabelMap = useMemo(() => {
    const m: Record<string, string> = {};
    // The FULL catalogue, not just the offerable subset: a stored row on a
    // no-longer-offerable built-in (`seating`, `companions`, …) still resolves,
    // and keying only the pickable ones left OptionInput falling back to the raw
    // internal name — in every locale.
    for (const f of fields) {
      m[getFieldKey(f)] = f.additionalField
        ? f.translation || f.name
        : tFields(f.name);
    }
    // Two options can legitimately carry the SAME label (a custom field named
    // like a built-in — `state` and a custom "Status"). OptionInput's
    // strictOption blur resolves by label and commits the FIRST match, so an
    // ambiguous label silently scopes the role on the other field. Suffix the
    // CUSTOM side with its group name; built-in labels stay untouched.
    const seen = new Map<string, number>();
    for (const label of Object.values(m)) {
      seen.set(label, (seen.get(label) ?? 0) + 1);
    }
    for (const f of fields) {
      if (!f.additionalField) continue;
      const key = getFieldKey(f);
      if ((seen.get(m[key]) ?? 0) > 1) {
        m[key] = `${m[key]} (${tGroups("additional")})`;
      }
    }
    // A row the backend can no longer resolve has to READ like one. The marker
    // used to require the QUALIFIED shape — but the authorizer judges the
    // NORMALIZED key, and normalization prefixes any unknown bare key, so a bare
    // `tier` (field deleted) and a legacy `seating` row are judged exactly like
    // `additionalFields.tier` and collapse the role's whole scope to zero guests.
    // Qualified-only meant that on real data — every stored row predates this
    // wave and is therefore bare — the marker never appeared at all: the row
    // rendered as an ordinary translated field ("Seat", "Status") while nulling
    // the role's visibility. `rowFilterKeyVerdict` mirrors the backend's rule, so
    // the five filterable built-ins with no FIELDS_GUEST def (`acceptanceState`,
    // `submittedAt`, `mainGuest`, `eventId`, `wasWaitlisted`) are still NOT
    // condemned — calling those "deleted" would invite the admin to remove a
    // scope that is being enforced.
    //
    // Two different labels because they are two different claims: a qualified key
    // can only have come from a custom field, so "deleted" is a fact; a bare one
    // may never have been a filter key at all, and saying "deleted" there would
    // be a guess.
    // `unjudged` is marked too: a dotted non-`additionalFields.` key
    // (`seatInfo.roomId`, only reachable on data written straight through the API)
    // is neither repaired nor sentinel'd — the validating lanes 400 while the
    // others read null and open the scope. That is the one shape the fail-closed
    // rule does not cover, so the least the editor can do is not pretend it is a
    // field.
    if (filter.key && rowFilterKeyVerdict(filter.key, fields) !== "resolvable") {
      m[filter.key] = filter.key.startsWith(ADDITIONAL_FIELDS_PREFIX)
        ? tRules("deletedFieldLabel", { name: bareFieldName(filter.key) })
        : tRules("unknownFilterFieldLabel", { name: filter.key });
    }
    return m;
  }, [fields, filter.key, tFields, tGroups, tRules]);
  // Semantic sections for the field picker — built here (not in the parents)
  // so RolePreviewCapabilityEditor's reuse gets them for free. Grouping keys on
  // the wire key so it lines up with `fieldOptions`; an ungrouped option renders
  // as a headerless leftover, never dropped.
  const fieldOptionGroups = useMemo(
    () =>
      buildGuestFieldOptionGroups(
        pickableFields,
        getFieldKey,
        (key) => tGroups(key),
      ),
    [pickableFields, tGroups],
  );
  // The field currently picked in column 1 (if it resolves to a known def).
  // `findFieldByKey` resolves BOTH shapes, so a template still carrying a legacy
  // bare key opens with its field correctly selected. Resolved against the FULL
  // catalogue, not just the pickable subset, so a legacy row on a
  // no-longer-offerable field still renders its label rather than reading as
  // deleted.
  const selectedField = filter.key
    ? findFieldByKey(fields, filter.key)
    : undefined;
  // The last field that actually RESOLVED — the operand-survival check compares
  // against THIS, never against `selectedField` alone.
  //
  // `filter.key` is not a safe source on its own: emptying the field box commits
  // `key: ""` (OptionInput's blur does that for an optional field), which makes
  // `selectedField` undefined. The survival rule keeps the operand whenever a
  // side is unresolved — deliberately, since an unresolved field proves nothing —
  // so without this ref the ORIGINAL bug came back in two steps: clear the box,
  // then pick an option-typed field, and the old free-text operand rode along.
  const lastResolvedField = useRef<Field | undefined>(selectedField);
  useEffect(() => {
    if (selectedField) lastResolvedField.current = selectedField;
  }, [selectedField]);
  // "is empty" / "is not empty" take no operand — drop the value cell entirely,
  // mirroring the guest-list filter row.
  const noValue = isNoValueOperator(filter.operator);
  // Enumerated field → propose its options instead of free text.
  const usePicker =
    !!selectedField && OPTION_VALUE_TYPES.has(selectedField.dataType);
  // The operand's own verdict. A REMOVED option is the one case no cascade can
  // repair — leaving the row is wrong for `≠` (it now matches everyone) and
  // deleting it is wrong for `=` (an empty row-filter list means unrestricted),
  // so the admin has to see what the row currently does and decide. The backend
  // now grandfathers such a row instead of 400-ing the save, which is exactly
  // why the editor has to say it out loud.
  const valueVerdict = rowFilterValueVerdict(
    filter.key,
    filter.operator,
    filter.value,
    fields,
  );
  const row = (
    <div
      className={`group flex gap-2 items-center rounded-lg transition-colors duration-500 ${
        flash ? "ring-2 ring-inset ring-(--color-outline-focus) surface-secondary" : ""
      }`}
    >
      <OptionInput
        inputRef={fieldRef}
        required={false}
        options={fieldOptions}
        sortOptions={true}
        optionLabels={fieldLabelMap}
        optionGroups={fieldOptionGroups}
        // The CANONICAL key, not the stored one: OptionInput marks an option
        // selected by string identity against its input value
        // (`isSelected = inputValue === option`), so a legacy bare key would
        // display raw and never highlight as the current pick.
        defaultValue={selectedField ? getFieldKey(selectedField) : filter.key}
        // Commit on SELECTION only. Without this OptionInput fires setParent on
        // every keystroke of the typeahead search, so a half-typed field name
        // ("E-MailV") reaches the handler as if it were a field change — which
        // the operand reset below would act on.
        commitOnSelectOnly
        // …and resolve on BLUR, which commitOnSelectOnly alone does NOT do:
        // without it the blur handler never calls setParent for typed text
        // (OptionInput:535-542), so a keyboard-only user could not set a field
        // at all and the box kept displaying a field the row was not filtering
        // on. `strictOption` matches the typed text against option values AND
        // labels, commits the match, and snaps back to the last valid value on
        // garbage (OptionInput:471-499).
        strictOption
        setParent={(v) => {
          // Mirror ConditionRow's field-change behaviour: operator survives
          // only while it stays valid for the new field's type, and the
          // operand only while the new field can still hold it (see
          // valueSurvivesFieldChange). Otherwise reset to the type's first
          // valid operator and clear the operand — an IMAGE pick would
          // otherwise leak "=" + free text into the stored filter, and a
          // text → picker switch would leave the old free text sitting in the
          // option dropdown as a phantom selection.
          const nextField = findFieldByKey(fields, v);
          // Belt and braces for the paths that bypass commitOnSelectOnly (the
          // blur handler commits an emptied box directly): a key that resolves
          // to no field is not a field change, so touch nothing but the key.
          // Resetting off an unresolvable key destroyed the operand before the
          // user had even picked their field, with no way to get it back.
          if (!nextField) {
            onUpdate({ key: v });
            return;
          }
          const glyphs = validGlyphsForField(nextField);
          const keepOperator = glyphs.includes(filter.operator);
          const keepValue = valueSurvivesFieldChange(
            lastResolvedField.current,
            nextField,
            filter.value,
            filter.operator,
          );
          onUpdate({
            key: v,
            ...(keepOperator ? {} : { operator: glyphs[0] ?? "=" }),
            ...(keepValue ? {} : { value: "" }),
          });
        }}
        width="flex-1"
        labelMargin=""
      />
      <OptionInput
        inputRef={opRef}
        required={false}
        options={operatorGlyphsForField(selectedField, filter.operator)}
        optionLabels={operatorLabels}
        defaultValue={filter.operator}
        // "is empty" / "is set" take no operand: the old one goes with the
        // switch (audit r4) — it would come back under the next operator.
        setParent={(v) =>
          onUpdate(isNoValueOperator(v) ? { operator: v, value: "" } : { operator: v })
        }
        // Slim box while a math symbol is picked, wider fixed box for word
        // operators (never w-auto — intrinsic input width balloons the box).
        width={
          isStandardOperator(GLYPH_TO_TOKEN[filter.operator] ?? "")
            ? "w-18 shrink-0"
            : "w-40 shrink-0"
        }
        labelMargin=""
      />
      {noValue || selectedField?.dataType === DataType.IMAGE ? (
        // No operand for ∅ / ⊙ — keep the row layout balanced with a spacer so
        // the trash icon stays put. IMAGE fields render no value input under
        // ANY operator (only presence checks apply; a legacy row carrying a
        // different stored operator must not get a free-text operand either).
        <div className="flex-1" />
      ) : usePicker ? (
        // Same value cell the guest list renders, in always-editable filter
        // mode: the selected field's options are proposed as a dropdown.
        <div className="flex-1">
          <TableCellContent
            field={selectedField!}
            isFilter
            isSelected
            isEditMode
            // Uncolored picker (no status-chip tinting), matching the guest-list
            // filter bar (see InputValue.tsx) — the row-filter value cell is a
            // plain selector, not a colored status badge.
            blank
            value={filter.value}
            onChangeValueAction={(v) => onUpdate({ value: v })}
            border="border"
            rounded="rounded-[10px]"
            height="h-9"
            fontSize="body-2-regular"
            surface="surface-primary"
            padding="px-2 py-1"
          />
        </div>
      ) : (
        <FormInput
          id={`row-filter-value-${idx}`}
          placeholder={valuePlaceholder}
          value={
            Array.isArray(filter.value) ? filter.value.join(",") : filter.value
          }
          onChangeAction={(v) => onUpdate({ value: v })}
          width="flex-1"
          margin=""
        />
      )}
      {/* Hover/focus-reveal like ConditionRow — the quiet default row shows
          no icon chrome until the user engages with it. */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={tRules("removeCondition")}
        className="hidden group-hover:inline-flex group-focus-within:inline-flex p-1.5 rounded-lg hover:surface-tertiary transition-colors cursor-pointer shrink-0"
      >
        <IconTrash size={14} className="font-tertiary hover:font-error" />
      </button>
    </div>
  );
  if (valueVerdict === "live") return row;
  return (
    <div className="flex flex-col gap-1">
      {row}
      {/* Not an error style: nothing here is invalid and the save goes
          through. It is a statement of what the row does now, which is the
          whole point — the silent version of this is what the wave exists to
          end. The `≠` case is the dangerous one, so it gets the warning tone
          and `=` the quieter tertiary. */}
      <p
        role="status"
        className={`body-3-regular pl-1 ${
          valueVerdict === "selectsEveryone" ? "font-warning" : "font-tertiary"
        }`}
      >
        {tRules(
          valueVerdict === "selectsEveryone"
            ? "rowFilterValueGoneSelectsEveryone"
            : "rowFilterValueGoneSelectsNobody",
          {
            value: Array.isArray(filter.value)
              ? filter.value.join(", ")
              : (filter.value ?? ""),
          },
        )}
      </p>
    </div>
  );
}

/** Stable comparison shape for the per-list defaults: sorted by surface, with
 *  absent ids normalized, so only a real change arms Save. */
function sortSurfaceDefaults(
  defaults: RoleSurfaceDefault[] | undefined,
): Array<{ surface: string; tableViewId: string; tableFilterId: string }> {
  return [...(defaults ?? [])]
    .map((d) => ({
      surface: d.surface,
      tableViewId: d.tableViewId ?? "",
      tableFilterId: d.tableFilterId ?? "",
    }))
    .sort((a, b) => a.surface.localeCompare(b.surface));
}

/**
 * What the role editor page reaches into the editor for (members plan B8):
 * the leave guard's "Save" runs the same submit as the footer.
 */
export type RoleTemplateEditorHandle = {
  /**
   * Save as the footer does. Never navigates — the caller decides where to
   * go. `false` when nothing was saved: Save is not possible (no name, no
   * capability, nothing changed, saving, read-only), the save would clear the
   * last row restriction, or the host's `onSave` refused.
   */
  submit: () => Promise<boolean>;
  /**
   * Why the last `submit` refused, when the editor itself refused (null: the
   * host did, or it saved): the `errors.*` code where there is one — so the
   * leave guard's dialog translates it once — and the text.
   */
  refusal: () => RoleTemplateRefusal | null;
};

export type RoleTemplateRefusal = { code: string | null; text: string };

/**
 * B2: the loud guest field catalogue the host read for this editor
 * (`getGuestFieldCatalogue`). `entries` null while loading or after a failure
 * — "All except" is then read-only (no orphan marking, no question it cannot
 * ask), the mode switch is off and its retry is offered.
 */
export type RoleTemplateFieldCatalogue = {
  entries: readonly CatalogueEntry[] | null;
  status: FieldCatalogueStatus;
  retry?: () => void;
  /** A retry (or any re-read) is out — the retry button shows it (U-LOW-4). */
  retrying?: boolean;
  /**
   * B2 F1 (S-MED-1, contract §8): read the catalogue again, UNCACHED, right
   * before an "All except" save — the sensitive question is asked again on
   * what that read says. Throws on failure. Absent: the editor re-checks
   * against `entries`.
   */
  refresh?: () => Promise<readonly CatalogueEntry[]>;
};

/** The editor's field scope: the B2 reducer + "adopt the server's copy after a save". */
type EditorScopeAction = FieldScopeAction | { type: "adopt"; from: FieldScopeSource };
function editorScopeReducer(s: FieldScopeState, a: EditorScopeAction): FieldScopeState {
  return a.type === "adopt" ? initialFieldScope(a.from) : fieldScopeReducer(s, a);
}

/**
 * The dirty-snapshot shape of a scope: what a save would send, exclusions
 * sorted (their order is no change the admin made).
 */
function scopeSnapshot(p: FieldScopePayload): FieldScopePayload {
  return p.fieldExclusions ? { ...p, fieldExclusions: [...p.fieldExclusions].sort() } : p;
}

/** A save answered with the server's copy (`onSave` may resolve a template instead of `true`). */
function isSavedTemplate(v: unknown): v is EventRoleTemplate {
  return !!v && typeof v === "object" && "eventRoleTemplateId" in v;
}

/**
 * The role template editor — the body of the role editor page.
 *
 * Sections (cards):
 *  1. Name and description
 *  2. Capabilities (CapabilitySwitchList)
 *  3. Row filters (custom OptionInput-based pickers)
 *  4. Visible fields (FieldAllowlistDualPane)
 *  5. List defaults (SurfaceDefaultsSection)
 *  6. Slotting visibility
 * and the sticky footer (Cancel / Save).
 *
 * REQUIRES `<FieldsProvider>` in scope — the role editor page wraps it.
 */
export function RoleTemplateEditor({
  initial,
  eventId,
  customFields,
  onSave,
  onCancel,
  saving,
  readOnly = false,
  readOnlyReason,
  onDirtyChange,
  onSaved,
  handleRef,
  saveBlockedReason,
  fieldCatalogue,
}: {
  initial?: EventRoleTemplate;
  /** Event scope — used to load the guest list's saved filters / views. */
  eventId: string;
  /** Custom additionalFields names + already-resolved display labels from FieldsProvider. */
  customFields: { name: string; label: string }[];
  /**
   * Persist the template. Return `true` — or the server's copy of the saved
   * template (B2) — on a successful save so the editor can re-baseline its
   * dirty state (Save disables again) and stay open like the other edit
   * modals; with the copy it also adopts the list and the exclusions the
   * server stored (orphans dropped, exclusions normalised). Any other return
   * (void / false) leaves the dirty state untouched. The host owns close
   * behaviour.
   */
  onSave: (
    body: EventRoleTemplateCreationDTO,
  ) => void | boolean | EventRoleTemplate | Promise<void | boolean | EventRoleTemplate>;
  onCancel: () => void;
  saving?: boolean;
  /** Read-only (archived, plan-locked, no write right): the banner with the
   *  reason, Save off, and every input, switch and row control disabled so the
   *  form looks as read-only as it is — the capability search and the
   *  sections stay there to explore the role. */
  readOnly?: boolean;
  /** The banner's text while read-only (default: the archived event's). */
  readOnlyReason?: string;
  /** The editor's own dirty state, as it changes — the host's leave guard reads it. */
  onDirtyChange?: (dirty: boolean) => void;
  /** After the footer's Save went through (never after the handle's submit) — the host decides where to go. */
  onSaved?: () => void;
  /** The submit handle for the host's leave guard. */
  handleRef?: Ref<RoleTemplateEditorHandle>;
  /** Saving cannot work at all (the role was deleted meanwhile, or changed elsewhere): Save stays off and the footer says this. */
  saveBlockedReason?: string;
  /** B2: the loud guest field catalogue (absent: none — "All except" stays read-only). */
  fieldCatalogue?: RoleTemplateFieldCatalogue;
}) {
  const t = useTranslations("roleTemplates");
  const tCommon = useTranslations("eventSettings");
  const tFields = useTranslations("guest-fields");
  const tErrors = useTranslations("errors");
  const tRoot = useTranslations();
  const { allFields } = useFields();
  const { showToast } = useToast();
  const [importKind, setImportKind] = useState<"filter" | "view" | null>(null);
  // Keys of row filters just imported — drives a brief highlight.
  const [flashKeys, setFlashKeys] = useState<Set<string>>(new Set());

  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [caps, setCaps] = useState<Set<Capability>>(
    new Set(initial?.capabilities ?? []),
  );
  const [rowFilters, setRowFilters] = useState<Filter[]>(
    initial?.rowFilters ?? [],
  );
  // The row filters the server holds for this role, as far as this editor
  // knows: the loaded ones, then the ones each confirmed save sent.
  const persistedRows = useRef<Filter[]>(initial?.rowFilters ?? []);
  // B2: the field scope — list (the authz truth), mode, exclusions and the
  // session memory — through the ONE reducer every writer uses.
  const [scope, dispatchScope] = useReducer(editorScopeReducer, initial, (i) =>
    initialFieldScope(i ?? {}),
  );
  const fieldAllowlist = scope.list;
  const scopeExclude = scope.list !== null && scope.mode === "exclude";
  const catalogueEntries = fieldCatalogue?.entries ?? null;
  const catalogueReady = fieldCatalogue?.status === "ready" && !!catalogueEntries;
  // "All except" cannot ask its sensitive question without the catalogue: the
  // view import and the prerequisite grants wait for it.
  const scopeWritesHeld = scopeExclude && !catalogueReady;
  const { ask: askScope, dialog: scopeDialog } = useFieldScopeConfirm();
  // The save's own re-check (S-MED-1) — raised: it can open over the leave
  // guard's dialog when that dialog's "Save" runs this editor's submit.
  const { ask: askRecheck, dialog: recheckDialog } = useFieldScopeConfirm({ elevated: true });
  /**
   * B2 F1 (S-MED-1): the field list the server holds as far as this editor
   * knows — the seed, then each confirmed save. A key on the list that is not
   * on this one was ADDED in this session, and an "All except" save asks the
   * sensitive question about it again on a fresh catalogue.
   */
  const baseListRef = useRef<readonly string[] | null>(initial?.fieldAllowlist ?? null);
  /**
   * Keys the admin let into Visible through the sensitive question in this
   * session, each at the level it was asked at (the catalogue of the render
   * that asked — F3 R3-LOW-2: at another level later it is asked about again).
   */
  const confirmedRef = useRef<SensitiveConfirmations>(new Map());
  const noteConfirmed = (keys: readonly string[]) =>
    noteSensitiveConfirmed(confirmedRef.current, keys, catalogueEntries ?? []);
  /** The re-check before an "All except" save is out: Save shows busy and refuses a second click. */
  const [rechecking, setRechecking] = useState(false);
  const submitInFlight = useRef(false);
  /**
   * B2 F3 (audit r3 R3-L2-2): the editor is still on screen. A re-check that
   * ends after it is gone (a leave elsewhere → Discard) never sends the save.
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [slottingScope, setSlottingScope] = useState<SlottingScopeAllowlists>({
    visibleOfferingIds: initial?.visibleOfferingIds ?? [],
    visibleTreatmentIds: initial?.visibleTreatmentIds ?? [],
    visibleLaneIds: initial?.visibleLaneIds ?? [],
  });
  const [surfaceDefaults, setSurfaceDefaults] = useState<RoleSurfaceDefault[]>(
    initial?.surfaceDefaults ?? [],
  );
  // B2 F1 (K-MED-1): the field scope and the rows as they are NOW — a save's
  // answer lands after an await, and the field list (and the rows) stay
  // editable while it is out. The answer is adopted only into the very state
  // the body was built from.
  const scopeNowRef = useRef(scope);
  scopeNowRef.current = scope;
  const rowsNowRef = useRef(rowFilters);
  rowsNowRef.current = rowFilters;

  // Dirty tracking — mirror the useState seeds above EXACTLY so a freshly
  // opened editor reads as pristine (Save disabled) until the user actually
  // edits something. `caps` is a Set (JSON.stringify-opaque) so it is compared
  // as a sorted array; everything else compares structurally. Snapshot fields
  // are the raw editor state, NOT the trimmed/expanded values submit() sends —
  // comparing like-for-like keeps "type then undo" from reading as a change.
  const initialSnapshot = useMemo(
    () => ({
      name: initial?.name ?? "",
      description: initial?.description ?? "",
      caps: [...new Set(initial?.capabilities ?? [])].sort(),
      rowFilters: initial?.rowFilters ?? [],
      // Canonical shape on BOTH sides: the pane emits a column with its
      // projection token, a stored list may predate it — add-then-remove must
      // land back on "pristine", not on a Save armed by plumbing. B2: what the
      // save would send — list (its mode's canonicaliser: "All except" keeps
      // stray tokens), mode and exclusions.
      fieldScope: scopeSnapshot(payloadOf(initialFieldScope(initial ?? {}))),
      slottingScope: {
        visibleOfferingIds: [...(initial?.visibleOfferingIds ?? [])].sort(),
        visibleTreatmentIds: [...(initial?.visibleTreatmentIds ?? [])].sort(),
        visibleLaneIds: [...(initial?.visibleLaneIds ?? [])].sort(),
      },
      // Sorted: the section rewrites its list per edit, so row ORDER is not a
      // change the admin made — comparing it would arm Save for nothing.
      surfaceDefaults: sortSurfaceDefaults(initial?.surfaceDefaults),
    }),
    [initial],
  );
  const currentSnapshot = useMemo(
    () => ({
      name,
      description,
      caps: [...caps].sort(),
      rowFilters,
      fieldScope: scopeSnapshot(payloadOf(scope)),
      slottingScope: {
        visibleOfferingIds: [...(slottingScope.visibleOfferingIds ?? [])].sort(),
        visibleTreatmentIds: [...(slottingScope.visibleTreatmentIds ?? [])].sort(),
        visibleLaneIds: [...(slottingScope.visibleLaneIds ?? [])].sort(),
      },
      surfaceDefaults: sortSurfaceDefaults(surfaceDefaults),
    }),
    [name, description, caps, rowFilters, scope, slottingScope, surfaceDefaults],
  );
  const { hasChanges, reset: resetDirtyBaseline } = useFormChanges(
    initialSnapshot,
    currentSnapshot,
  );
  // Lifted for the host's leave guard; a confirmed save re-baselines below
  // (`resetDirtyBaseline`), which reports `false` again.
  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);
  // …and an editor that goes away takes its dirty state with it: the host's
  // guard must not stay armed over a form that is no longer there.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  // The slotting visibility allowlist only NARROWS what a role sees inside
  // Slotting — it does not grant access. Without slotting:read (directly or
  // implied by slotting:write/delete) the whole Slotting section is hidden, so
  // the allowlist silently does nothing. Warn the admin when that's the case.
  const hasSlottingRead = useMemo(
    () => expandImplied(caps).has("slotting:read" as Capability),
    [caps],
  );
  // Lets the "no Slotting access" hint jump the admin to the capabilities grid
  // AND grant slotting:read in one click, so the pickers appear immediately.
  const capsSectionRef = useRef<HTMLElement | null>(null);
  const grantSlottingAccess = () => {
    setCaps((prev) => new Set([...prev, "slotting:read" as Capability]));
    // Scroll the SLOTTING capability group itself (not the whole, viewport-taller
    // capabilities section) into the centre of the modal, so the just-enabled
    // slotting:read toggle is clearly visible. Targeting the tall section instead
    // edge-aligns to its top ("start" = over-scroll) or bottom ("nearest" =
    // under-scroll) and never lands on the actual toggle. The grid tags each group
    // header id="cap-group-<group>" (a11y aria-labelledby); reuse it, fall back to
    // the section if the id scheme ever changes.
    const slottingGroup =
      typeof document !== "undefined"
        ? document.getElementById("cap-group-slotting")
        : null;
    (slottingGroup ?? capsSectionRef.current)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  // A ticked capability whose list ACTION reads a field the RESTRICTED
  // allowlist withholds: check-in without `checkedIn` renders a button that
  // always says "check in", approve/decline without `acceptanceState` reads
  // "state unknown". The action column itself is never allowlist-governed
  // (`ALLOWLIST_EXEMPT_FIELDS`), so the capability alone shows the button —
  // this is the one gap left, and it is closable in one click.
  const fieldPrerequisites = useMemo(
    () => missingFieldPrerequisites(caps, fieldAllowlist),
    [caps, fieldAllowlist],
  );
  // The picker's "Aktion" entry mirrors the lists' "Aktion" columns this role's
  // rights give it — or, for a role without any action right, the guest list's
  // own column (check-in). It stands for those capabilities AND the fields
  // their actions read, and one click turns on all of it. Always offered while
  // a part is missing — a read-only role finds it too, which is the whole
  // point: the admin looks for "Aktion" among the fields, not among the rights.
  const actionBundle = useMemo(() => effectiveActionBundle(caps), [caps]);
  const missingActionCaps = useMemo(
    () => missingActionCapsOf(actionBundle, caps),
    [actionBundle, caps],
  );
  // The pane asked its question (if any) before calling this.
  const grantAction = () => {
    setCaps((prev) => expandImplied(new Set([...prev, ...actionBundle.caps])));
    // `null` is unrestricted — every field is already visible; `addList` is a
    // no-op there. Through the reducer: "All except" stays "All except".
    dispatchScope({ type: "addList", keys: [...actionBundle.fields] });
  };
  const capabilityLabel = (cap: Capability): string =>
    tRoot(
      CAPABILITY_CATALOGUE.find((d) => d.key === cap)?.i18nKey ?? cap,
      { defaultValue: cap },
    );
  const fieldPrerequisiteWarnings = useMemo(() => {
    const m = new Map<Capability, string[]>();
    for (const { cap, field } of fieldPrerequisites) {
      m.set(cap, [...(m.get(cap) ?? []), tFields(field)]);
    }
    return m;
  }, [fieldPrerequisites, tFields]);
  /** An allowlist key's label (the confirm questions name the fields). */
  const fieldLabelOf = (key: string): string => {
    if (key.startsWith(ADDITIONAL_FIELDS_PREFIX)) {
      const bare = bareFieldName(key);
      const def = allFields.find((f) => f.additionalField && f.name === bare);
      return def?.translation || bare;
    }
    return tFields.has(key) ? tFields(key) : key;
  };
  // Asks in "All except" when the field needs it (the guard test keeps
  // `requiresFields` to NORMAL built-ins, so in practice it never does).
  const grantField = (field: string) => {
    if (fieldAllowlist === null || fieldAllowlist.includes(field)) return;
    const run = () => dispatchScope({ type: "addList", keys: [field] });
    const needs = scopeExclude ? needsSensitiveConfirm([field], catalogueEntries ?? []) : [];
    if (needs.length === 0) {
      run();
      return;
    }
    void askScope("sensitive", needs.map(fieldLabelOf)).then((ok) => {
      if (!ok) return;
      noteConfirmed(needs);
      run();
    });
  };

  // Bare-name label map — for ImportFromGuestListModal, which renders SAVED
  // GUEST-LIST filters and those store bare names. The row-filter picker builds
  // its own wire-key-keyed map inside RowFilterRow.
  const fieldLabelMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const f of allFields) {
      m[f.name] = f.additionalField ? (f.translation || f.name) : tFields(f.name);
    }
    return m;
  }, [allFields, tFields]);

  const addRowFilter = () =>
    setRowFilters([...rowFilters, { key: "", operator: "=", value: "" }]);
  const updateRowFilter = (idx: number, patch: Partial<Filter>) =>
    setRowFilters((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    );
  const removeRowFilter = (idx: number) =>
    setRowFilters(rowFilters.filter((_, i) => i !== idx));

  // Briefly highlight the given row-filter keys, then clear.
  const flash = (keys: string[]) => {
    if (keys.length === 0) return;
    setFlashKeys(new Set(keys));
    setTimeout(() => setFlashKeys(new Set()), 1500);
  };

  // Apply a saved guest-list filter or view from the import modal. `mode`
  // chooses append vs overwrite; payload is the filter conditions / view fields.
  const handleImport = (
    mode: "add" | "replace",
    payload: Filter[] | string[],
  ) => {
    if (importKind === "filter") {
      const incoming = payload as Filter[];
      setRowFilters((prev) => {
        // Canonicalize HERE, at the persisting call site: a saved guest-list
        // filter carries the bare custom-field name, a row filter must carry the
        // wire key. Both sides go through it so a legacy `tier` row already in
        // the template dedupes against an imported `additionalFields.tier`.
        const next =
          mode === "add"
            ? mergeRowFilters(prev, incoming, allFields)
            : replaceRowFilters(incoming, allFields);
        // Highlight the rows that are new relative to what was there — in the
        // SAME key space as `next`, which the merge canonicalized. Comparing raw
        // prev keys against canonical next keys made a pre-existing legacy row
        // flash as if it had just been imported.
        const before = new Set(
          mergeRowFilters([], prev, allFields).map(filterKey),
        );
        flash(next.map(filterKey).filter((k) => !before.has(k)));
        return next;
      });
    } else if (importKind === "view") {
      const fields = payload as string[];
      // A saved view's "Aktion" column is not a field — it follows a capability
      // and reads a field. Importing the view must bring BOTH along, or the role
      // gets a view whose "Aktion" column is empty: exactly the columns the view
      // carries, never the whole bundle. The modal's footer named this before
      // the click (`viewImportActivates`).
      const viaColumns = actionBundleForColumns(
        categorizeViewFields(fields, allFields).implicit,
      );
      const keys = [...viewFieldsToAllowlist(fields, allFields), ...viaColumns.fields];
      const grantCaps = () => {
        if (viaColumns.caps.length > 0) {
          setCaps((prev) => expandImplied(new Set([...prev, ...viaColumns.caps])));
        }
      };
      // The import dialog goes first: a question asked below opens on its own.
      setImportKind(null);
      const done = () => showToast(t("imported"), "success");
      // B2 rules. "All fields" + Add keeps today's behaviour (contract §3b.2):
      // the role narrows to the view's fields — a replace. "All except" + Add
      // stays "All except" (the sensitive question first); Replace there asks,
      // then switches to "Only these". "Only these" is unchanged.
      if (mode === "add" && fieldAllowlist !== null) {
        const needs = scopeExclude ? needsSensitiveConfirm(keys, catalogueEntries ?? []) : [];
        const apply = () => {
          grantCaps();
          dispatchScope({ type: "addList", keys });
          done();
        };
        if (needs.length === 0) apply();
        else
          void askScope("sensitive", needs.map(fieldLabelOf)).then((ok) => {
            if (!ok) return;
            noteConfirmed(needs);
            apply();
          });
        return;
      }
      const apply = () => {
        grantCaps();
        dispatchScope({ type: "replaceList", list: keys });
        done();
      };
      if (mode === "replace" && scopeExclude) {
        void askScope("replace", []).then((ok) => ok && apply());
        return;
      }
      apply();
      return;
    }
    showToast(t("imported"), "success");
    setImportKind(null);
  };

  // Require a real change before Save lights up. Without this an unchanged edit
  // (or just reopening a template) left Save active — see access-settings bug.
  // The capability every custom role has anyway shows on and locked — it is
  // no capability this role gives; at least one more must be switched on.
  const grantedCaps = [...caps].filter((c) => !CUSTOM_FOUNDATION_CAPABILITIES.has(c)).length;

  // Row filters are the role's data scope: a row is never dropped behind the
  // admin's back (audit r3 MED-1). A row serialization would drop is either a
  // placeholder (no field and no value — "Add filter" left alone) or
  // UNFINISHED (a field emptied while a value stays, a value-taking operator
  // without its operand — and not the exact row already stored). Unfinished
  // rows hold Save until they are completed or removed; placeholders are
  // dropped quietly — unless dropping them would leave fewer rows than the
  // server holds (a stored row emptied in both boxes), which is the same
  // silent widening. Measured against the editor's own record of the stored
  // rows (`persistedRows`), never the host's copy.
  const isBlankRow = (f: Filter) =>
    !f.key && (Array.isArray(f.value) ? f.value.length === 0 : !f.value);
  const droppedRows = rowFilters.filter(
    (f) => serializeRowFilters([f], allFields, persistedRows.current).length === 0,
  );
  const unfinishedRows = droppedRows.filter((f) => !isBlankRow(f)).length;
  const outgoingRows = serializeRowFilters(rowFilters, allFields, persistedRows.current).length;
  const rowsBlock =
    unfinishedRows > 0 ||
    (droppedRows.length > 0 && outgoingRows < persistedRows.current.length);

  // B2: the backend caps a list at 200 keys — measured on what the save sends.
  const overCap = exceedsCap(fieldAllowlist, scopeExclude ? "exclude" : "include");

  const canSave =
    name.trim().length > 0 &&
    grantedCaps > 0 &&
    !saving &&
    !rechecking &&
    hasChanges &&
    !saveBlockedReason &&
    !rowsBlock &&
    !overCap;

  // Why the last submit refused, when the editor itself refused.
  const refusalRef = useRef<RoleTemplateRefusal | null>(null);
  const refuse = (code: string | null, text: string): false => {
    refusalRef.current = { code, text };
    return false;
  };

  // What keeps Save off on a form that could be saved once filled in — said
  // in the footer, not left to guess.
  const missingInput: RoleTemplateRefusal | null = saveBlockedReason
    ? { code: null, text: saveBlockedReason }
    : !name.trim()
      ? { code: "nameRequired", text: tErrors("nameRequired") }
      : grantedCaps === 0
        ? { code: null, text: t("needsCapability") }
        : rowsBlock
          ? { code: null, text: t("completeFilterRows") }
          : overCap
            ? { code: null, text: t("fieldScope.capExceeded") }
            : null;

  /** The catalogue read uncached (`refresh`) — null when it failed; without `refresh`, `entries`. */
  const readCatalogueFresh = async (): Promise<readonly CatalogueEntry[] | null> => {
    if (!fieldCatalogue?.refresh) return catalogueEntries;
    try {
      return await fieldCatalogue.refresh();
    } catch {
      return null;
    }
  };

  /**
   * B2 F1 (S-MED-1): before an "All except" save, read the catalogue again
   * (uncached, `refresh`) and ask the sensitive question about every key this
   * session ADDED (list − `baseListRef`) that is now sensitive, access data
   * or of an unknown level and was not confirmed yet — a field raised (or
   * deleted and re-created as sensitive) since it was shown as a normal one
   * must not slip in with no question. Every "All except" save re-reads; a
   * failed read refuses only a save that adds keys. `true` = go on.
   *
   * B2 F2 (audit r2 A3): the question takes a while — after a confirm the
   * catalogue is read again and anything raised meanwhile is asked about too
   * (`recheckSensitiveAdds`, at most three questions).
   */
  const recheckSensitive = async (list: readonly string[]): Promise<boolean> => {
    const first = await readCatalogueFresh();
    const base = new Set(baseListRef.current ?? []);
    const outcome = await recheckSensitiveAdds({
      added: list.filter((k) => !base.has(k)),
      first,
      reread: readCatalogueFresh,
      ask: (keys) => askRecheck("sensitive", keys.map(fieldLabelOf)),
      confirmed: confirmedRef.current,
    });
    if (outcome === "ok") return true;
    const text =
      outcome === "failed"
        ? t("fieldScope.recheckFailed")
        : outcome === "capped"
          ? t("fieldScope.recheckCapped")
          : t("fieldScope.recheckDeclined");
    showToast(text, "error");
    return refuse(null, text);
  };

  /** `submit` past its guards — one at a time (`submitInFlight`). */
  const submitNow = async (): Promise<boolean> => {
    // What actually goes on the wire — flattened, coerced, and with the rows a
    // value-taking operator left empty removed. Computed once so the editor can
    // ADOPT it below instead of keeping its own, differently-shaped rows.
    // `persistedRows` is what is PERSISTED: an already-stored row with an
    // empty operand survives serialization instead of being dropped, because
    // dropping it would widen the role's scope on a save that never touched it.
    // The editor's OWN record of the stored rows — what it loaded, then what
    // each confirmed save sent — never the host's `initial`, which a failed
    // re-read can leave at a pre-save copy (audit r2 MED-1: the stale copy had
    // no rows, so clearing a row saved since skipped the guard below and the
    // role went unrestricted with a success toast).
    const storedRowFilters = persistedRows.current;
    const wireRowFilters = serializeRowFilters(
      rowFilters,
      allFields,
      storedRowFilters,
    );
    // P1 — serialization dropping the LAST row filter would send `rowFilters: []`,
    // and an empty list IS "row-unrestricted" (`ResolvedAuthz.isRowUnrestricted`).
    // The role would go from scoped to seeing every guest in the event, with a
    // success toast — and its AI assistant would gain full tool access, which
    // gates on the same emptiness. Clearing the scope on purpose stays possible:
    // remove the row instead of emptying its operand.
    //
    // Three clauses, and all three are load-bearing:
    //  · something must actually be LOST — `storedRowFilters.length > 0`. Without
    //    it a template with no row filters at all (the whole CREATE flow included)
    //    refused the save as soon as the admin clicked "add condition" and left
    //    the blank row alone: 1 editor row in, 0 wire rows out, so the guard threw
    //    away the name/capability edit while announcing the removal of a
    //    restriction that never existed.
    //  · a ROW must still be on screen — any row, also one whose field box was
    //    emptied (audit r3 MED-1: "a row with a field" let exactly that row slip
    //    through and the role went unrestricted). Removing the rows is the
    //    deliberate way to unrestrict a role, and it must keep working — it is
    //    the escape hatch the toast points at.
    //  · and nothing may survive serialization.
    // (The unfinished-row block above stops these saves before they get here;
    // this stays as the last line in front of the wire.)
    if (
      storedRowFilters.length > 0 &&
      rowFilters.length > 0 &&
      wireRowFilters.length === 0
    ) {
      showToast(tErrors("rowFilterScopeWouldBeCleared"), "error");
      return refuse("rowFilterScopeWouldBeCleared", tErrors("rowFilterScopeWouldBeCleared"));
    }
    // The state the body is built from — the answer is adopted into the
    // editing state only while it is still exactly this (K-MED-1).
    const builtScope = scope;
    const builtRows = rowFilters;
    const scopePayload = payloadOf(builtScope);
    if (scopePayload.fieldAllowlistMode === "exclude") {
      // N3: the field rows are locked while it runs (no second question can
      // stack on its own); they open again before the PUT (K-MED-1 keeps the
      // edits made while that is out).
      setRechecking(true);
      let go: boolean;
      try {
        go = await recheckSensitive(scopePayload.fieldAllowlist ?? []);
      } finally {
        setRechecking(false);
      }
      if (!go) return false;
      // Gone meanwhile (the edits were discarded): nothing goes out.
      if (!mountedRef.current) return false;
    }
    const ok = await onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      // Strip owner-only caps defensively — the picker no longer offers them,
      // but a malformed/legacy `initial` could still carry one and the backend
      // would reject the whole save with `ownerOnlyCapability`.
      capabilities: Array.from(expandImplied(caps)).filter(isAssignableCapability),
      rowFilters: wireRowFilters,
      // Canonical wire shape: a composite column's projection tokens ride along
      // (`seating` → `seatInfo`), whichever lane put the column here — the
      // picker, a view import, the prerequisite grant, or a template stored
      // before the tokens existed. See `ALLOWLIST_KEY_IMPLIES`. B2: "All
      // except" sends mode + normalised exclusions (never null) and keeps its
      // untouched stray tokens; "Only these" sends mode "include" (the backend
      // unsets the metadata); "All fields" sends `null` alone.
      ...scopePayload,
      // Full editor asserts the COMPLETE desired scope on every save, so always
      // send explicit arrays: [] clears to unrestricted, [ids] restricts. (Backend
      // update preserves only OMITTED visible* keys; this editor never omits.)
      visibleOfferingIds: slottingScope.visibleOfferingIds ?? [],
      visibleTreatmentIds: slottingScope.visibleTreatmentIds ?? [],
      visibleLaneIds: slottingScope.visibleLaneIds ?? [],
      // Same "complete desired state" rule as the scope lists above: always an
      // explicit array, so removing the last default actually clears it (the
      // backend preserves only an OMITTED key).
      surfaceDefaults,
    });
    // On a confirmed save, re-baseline to the just-saved state so Save disables
    // again and the modal can stay open — the norm for edit modals here. The
    // host decides whether to also close (create flow closes; edit stays open).
    //
    // Re-baseline to what the SERVER got, not to what the editor happened to
    // hold: serialization drops empty value-taking rows and rewrites multi-picks
    // and "—" picks. Baselining the raw rows left the dropped/rewritten ones on
    // screen with Save greyed out — a scope the admin believes is in force and
    // the server never received, and the lie only surfaces on the next reopen.
    if (ok === true || isSavedTemplate(ok)) {
      // B2 F1 (K-MED-1): the rows and the field list stay editable while the
      // save is out. An edit made meanwhile is the admin's newer intent: it
      // stays on screen, and only the BASELINE moves to what the server got —
      // Save stays armed for it. Untouched since the body ⇒ adopt as before.
      if (rowsNowRef.current === builtRows) setRowFilters(wireRowFilters);
      // B2: the server's copy of the list, mode and exclusions (it drops
      // orphaned custom keys and normalises the exclusions) is the baseline —
      // never the editor's guess of it.
      let fieldScope = scopeSnapshot(scopePayload);
      if (isSavedTemplate(ok) && "fieldAllowlist" in ok) {
        fieldScope = scopeSnapshot(payloadOf(initialFieldScope(ok)));
        if (scopeNowRef.current === builtScope) dispatchScope({ type: "adopt", from: ok });
        baseListRef.current = Array.isArray(ok.fieldAllowlist) ? [...ok.fieldAllowlist] : null;
      } else {
        baseListRef.current = scopePayload.fieldAllowlist;
      }
      resetDirtyBaseline({ ...currentSnapshot, rowFilters: wireRowFilters, fieldScope });
      persistedRows.current = wireRowFilters;
      return true;
    }
    return false;
  };

  const submit = async (): Promise<boolean> => {
    refusalRef.current = null;
    if (readOnly) {
      return readOnlyReason
        ? refuse(null, readOnlyReason)
        : refuse("eventArchivedReadOnly", tErrors("eventArchivedReadOnly"));
    }
    if (submitInFlight.current) return refuse(null, t("saving"));
    if (!canSave) {
      if (missingInput) return refuse(missingInput.code, missingInput.text);
      return saving ? refuse(null, t("saving")) : refuse("unknown", tErrors("unknown"));
    }
    submitInFlight.current = true;
    try {
      return await submitNow();
    } finally {
      submitInFlight.current = false;
    }
  };

  useImperativeHandle(handleRef, () => ({ submit, refusal: () => refusalRef.current }));

  const tMembers = useTranslations("members");

  return (
    <div className="flex flex-col gap-6" data-testid="role-template-editor">
      {readOnly && (
        <div className="px-4 py-3 rounded-lg border outline-quaternary surface-secondary body-3-regular font-secondary" data-testid="role-template-read-only">
          {readOnlyReason ?? tErrors("eventArchivedReadOnly")}
        </div>
      )}
      <SectionCard title={t("identityTitle")} testId="role-template-identity">
        <div className="flex flex-col gap-3">
          {initial?.systemSeeded && initial?.seedKey ? (
            <p className="body-3-regular font-tertiary">{t("systemSeededHint")}</p>
          ) : null}
          <FormInput
            id="role-template-name"
            label={t("name")}
            value={name}
            onChangeAction={setName}
            disabled={readOnly}
          />
          <MultilineInput
            id="role-template-description"
            label={t("description")}
            value={description}
            onChangeAction={setDescription}
            rows={3}
            disabled={readOnly}
          />
        </div>
      </SectionCard>

      <section ref={capsSectionRef}>
        <SectionCard title={t("capabilities")} info={t("capabilitiesInfo")} testId="role-template-capabilities">
          <CapabilitySwitchList
            value={caps}
            onChange={setCaps}
            fieldPrerequisiteWarnings={fieldPrerequisiteWarnings}
            disabled={readOnly}
          />
        </SectionCard>
      </section>

      <SectionCard
        title={t("rowFilters")}
        testId="role-template-row-filters"
        headerRight={
          // Hidden on an archived event, like the pane's offer and the warning
          // CTA: an import now also ticks rights, and it must not be the back
          // door that dirties a form nobody can save.
          !readOnly ? (
            <SecondaryButton
              size="small"
              width="w-max"
              icon={IconDownload}
              text={t("importCta")}
              onClick={() => setImportKind("filter")}
            />
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2">
          <p className="body-3-regular font-tertiary">{t("rowFiltersHint")}</p>
          {/* Read-only: the rows show what the role is scoped to; a disabled
              fieldset turns every control inside them off at once. */}
          <fieldset disabled={readOnly} className="m-0 p-0 border-0 min-w-0 flex flex-col gap-2" data-testid="role-template-row-filter-rows">
            {rowFilters.map((f, idx) => (
              <RowFilterRow
                key={idx}
                filter={f}
                idx={idx}
                fields={allFields}
                valuePlaceholder={t("rowFilterValuePlaceholder")}
                flash={flashKeys.has(filterKey(f))}
                onUpdate={(patch) => updateRowFilter(idx, patch)}
                onRemove={() => removeRowFilter(idx)}
              />
            ))}
          </fieldset>
          {!readOnly && (
            <SecondaryButton
              size="small"
              width="w-max"
              icon={IconPlus}
              text={t("addRowFilter")}
              onClick={addRowFilter}
            />
          )}
        </div>
      </SectionCard>

      <SectionCard
        title={t("fieldAllowlist")}
        testId="role-template-visible-fields"
        headerRight={
          // Hidden on an archived event, like the pane's offer and the warning
          // CTA: an import now also ticks rights, and it must not be the back
          // door that dirties a form nobody can save.
          !readOnly ? (
            <SecondaryButton
              size="small"
              width="w-max"
              icon={IconDownload}
              text={t("importCta")}
              onClick={() => setImportKind("view")}
              disabled={scopeWritesHeld || rechecking}
            />
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2">
          {/* B2 F2 (N3): locked while the save's re-check runs, like read-only. */}
          <fieldset
            disabled={readOnly || rechecking}
            aria-busy={rechecking || undefined}
            className="m-0 p-0 border-0 min-w-0"
            data-testid="role-template-visible-fields-pane"
          >
            <FieldAllowlistDualPane
              value={fieldAllowlist}
              onChange={(next) =>
                dispatchScope(next === null ? { type: "setAllFields", on: true } : { type: "replaceList", list: next })
              }
              mode={scope.mode}
              exclusions={scope.exclusions}
              onScopeAction={dispatchScope}
              modeSwitch
              customFields={customFields}
              actionBundle={actionBundle}
              missingActionCaps={missingActionCaps}
              onGrantAction={grantAction}
              readOnly={readOnly}
              catalogue={catalogueEntries}
              catalogueStatus={fieldCatalogue?.status ?? "failed"}
              onRetryCatalogue={fieldCatalogue?.retry}
              catalogueRetrying={fieldCatalogue?.retrying}
              onSensitiveConfirmed={noteConfirmed}
            />
          </fieldset>
          {fieldPrerequisites.map(({ cap, field }) => (
            <div
              key={`${cap}:${field}`}
              role="status"
              className="flex flex-col items-start gap-2 px-3 py-2 rounded-xl surface-secondary"
            >
              <span className="body-3-regular font-tertiary">
                {t("fieldPrerequisiteWarning", {
                  capability: capabilityLabel(cap),
                  field: tFields(field),
                })}
              </span>
              {!readOnly && (
                <SecondaryButton
                  size="small"
                  width="w-max"
                  text={t("fieldPrerequisiteGrantCta", { field: tFields(field) })}
                  onClick={() => grantField(field)}
                  disabled={scopeWritesHeld || rechecking}
                />
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={tMembers("advancedTitle")} testId="role-template-defaults">
        <SurfaceDefaultsSection
          eventId={eventId}
          value={surfaceDefaults}
          onChange={setSurfaceDefaults}
          disabled={readOnly}
        />
      </SectionCard>

      <SectionCard title={t("slottingScope")} testId="role-template-slotting">
        {hasSlottingRead ? (
          // The visibility filter only NARROWS within Slotting, so it's only
          // meaningful once the role can read Slotting at all — show the pickers
          // only then. The stored scope is preserved if the capability is later
          // removed (it just becomes inert until re-granted).
          <RoleTemplateScopePickers
            eventId={eventId}
            scopes={slottingScope}
            onChange={setSlottingScope}
            disabled={readOnly}
          />
        ) : (
          <div className="flex flex-col items-start gap-2 px-3 py-2 rounded-xl surface-secondary">
            <span className="body-3-regular font-tertiary">
              {t("slottingScopeNoCapWarning")}
            </span>
            {!readOnly && (
              <SecondaryButton
                size="small"
                width="w-max"
                text={t("slottingScopeGrantCta")}
                onClick={grantSlottingAccess}
              />
            )}
          </div>
        )}
      </SectionCard>

      {scopeDialog}
      {recheckDialog}
      {importKind && (
        <ImportFromGuestListModal
          kind={importKind}
          eventId={eventId}
          allFields={allFields}
          fieldLabelMap={fieldLabelMap}
          currentRowFilters={rowFilters}
          currentAllowlist={fieldAllowlist}
          currentCapabilities={caps}
          onApply={handleImport}
          onClose={() => setImportKind(null)}
        />
      )}

      <StickyFormFooter
        summary={
          !readOnly && missingInput ? (
            <span data-testid="role-template-save-reason">{missingInput.text}</span>
          ) : undefined
        }
        secondary={{
          label: tCommon("cancel"),
          onClick: onCancel,
          // F3 (audit r3 R3-L2-2): off while the re-check runs, like Save —
          // Cancel → Discard then would leave a save behind that still goes out.
          disabled: saving || rechecking,
          testId: "role-template-cancel",
        }}
        primary={{
          label: saving || rechecking ? t("saving") : initial ? t("save") : t("create"),
          onClick: () => {
            void submit().then((ok) => {
              if (ok) onSaved?.();
            });
          },
          loading: saving || rechecking,
          disabled: !canSave || readOnly,
          // B2 F2 (N4): off but focusable while the re-check runs — its
          // question hands the focus back here (a natively disabled button
          // cannot take it, and it fell to <body>); a click then is a no-op.
          // F3 (audit r3 R3-L2-1): and while the save itself is out, so a
          // refused save (the "guest fields changed" line) leaves it here.
          focusableWhenDisabled: rechecking || saving,
          testId: "role-template-save",
        }}
      />
    </div>
  );
}
